// As-Built map view. MapLibre layers per as_built_* kind, plus plan-lane
// color overlay. Click any feature → detail panel slides in from the right.
//
// Layers:
//   - segments (LineString, colored by plan lane)
//   - drops    (LineString, thinner)
//   - poles, vaults, closures, structures, equipment (Points, different icons)
//
// Plan-lane toggles: each Vetro plan gets its own color. Operator toggles
// individual lanes on/off via the layer panel.

import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

type PlanLane = {
  source_plan_id: number;
  source_project_label: string | null;
  is_canonical: boolean;
};

type GeoJsonData = {
  segments: any;
  poles: any;
  vaults: any;
  closures: any;
  drops: any;
  structures: any;
  equipment: any;
  ducts: any;
  plan_lanes: PlanLane[];
};

// Deterministic color per plan_id — viridis-ish palette for max distinction
const LANE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ec4899', // pink
  '#8b5cf6', // purple
  '#ef4444', // red
  '#14b8a6', // teal
  '#f97316', // orange
  '#06b6d4', // cyan
  '#a855f7', // violet
];

function laneColor(planId: number, idx: number): string {
  return LANE_COLORS[idx % LANE_COLORS.length];
}

type FeatureClickInfo = {
  kind: string;
  id: string;
  mb_uid: string;
  source_plan_id: number | null;
  status: string;
  props: Record<string, any>;
};

export default function AsBuiltMap({
  projectId,
  height = 600,
  onFeatureClick,
}: {
  projectId: string;
  height?: number | string;
  onFeatureClick?: (f: FeatureClickInfo) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Layer visibility toggles
  const [visibleLayers, setVisibleLayers] = useState<Record<string, boolean>>({
    segments: true, drops: true, poles: true, vaults: true,
    closures: true, structures: true, equipment: true, ducts: true,
  });

  // Plan-lane visibility (planId → bool). Default: all visible.
  const [visibleLanes, setVisibleLanes] = useState<Record<number, boolean>>({});

  // Construction-readiness lookup: which permits block each segment.
  // Powers the per-segment permit popup that opens on click.
  const { data: readiness } = useQuery<any>({
    queryKey: ['as-built-readiness', projectId],
    queryFn: () => api.get(`/permits/projects/${projectId}/construction-readiness`),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  // Map: mb_uid → { ready, blocking_permits: [...] }. Keyed by mb_uid so it
  // matches feature.id (which is mb_uid after promoteId). We also key by
  // segment_id so callers with the database UUID can resolve.
  const segReadinessMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const s of (readiness?.segments ?? [])) {
      if (s.mb_uid) m.set(s.mb_uid, s);
      if (s.segment_id) m.set(s.segment_id, s);
    }
    return m;
  }, [readiness]);

  const { data, isLoading } = useQuery<GeoJsonData>({
    queryKey: ['asbuilt-geojson', projectId],
    queryFn: () => api.get(`/as-built/projects/${projectId}/geojson`),
    enabled: !!projectId,
  });

  // Initialize all plan lanes as visible on first data load
  useEffect(() => {
    if (data?.plan_lanes && Object.keys(visibleLanes).length === 0) {
      const init: Record<number, boolean> = {};
      for (const l of data.plan_lanes) init[l.source_plan_id] = true;
      setVisibleLanes(init);
    }
  }, [data?.plan_lanes]);

  // Plan color map keyed by source_plan_id, deterministic order from plan_lanes array
  const planColorMap = useMemo(() => {
    const m = new Map<number, string>();
    (data?.plan_lanes ?? []).forEach((l, i) => m.set(l.source_plan_id, laneColor(l.source_plan_id, i)));
    return m;
  }, [data?.plan_lanes]);

  // Initialize MapLibre once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          'osm-tiles': {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap',
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm-tiles' }],
      },
      center: [-80.45, 38.6],     // WV center; will fit-to-data once layers load
      zoom: 7,
    });
    mapRef.current = map;
    map.on('load', () => setMapReady(true));
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Wire data into the map every time it changes
  useEffect(() => {
    const mapInst = mapRef.current;
    if (!mapInst || !mapReady || !data) return;
    const map = mapInst;   // non-null narrowing for nested closures

    // Build a match expression to color features by source_plan_id
    // Default to grey if a feature has no plan
    function planColorExpr(): any {
      const expr: any[] = ['match', ['get', 'source_plan_id']];
      for (const [planId, color] of planColorMap.entries()) {
        expr.push(planId, color);
      }
      expr.push('#94a3b8'); // fallback
      return expr.length > 3 ? expr : '#3b82f6';
    }

    // Filter expression: only show features whose source_plan_id is enabled
    function laneFilterExpr(): any {
      const enabledPlanIds = Object.entries(visibleLanes)
        .filter(([, v]) => v)
        .map(([k]) => Number(k));
      if (enabledPlanIds.length === 0) return ['==', ['get', 'source_plan_id'], -1]; // hide all
      return ['in', ['get', 'source_plan_id'], ['literal', enabledPlanIds]];
    }

    // Helper: upsert source + layer.
    // promoteId tells MapLibre to use the `mb_uid` property as the feature
    // id (instead of the top-level GeoJSON `id`, which MapLibre drops for
    // non-numeric UUIDs). Without this, `feature.id` is undefined in click
    // handlers and `['in', ['id'], ...]` filters never match — so the red
    // glow layer renders nothing and per-segment readiness lookups fail.
    function upsertSource(id: string, fc: any, promoteIdField?: string) {
      const existing = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(fc);
      } else {
        const opts: any = { type: 'geojson', data: fc };
        if (promoteIdField) opts.promoteId = promoteIdField;
        map.addSource(id, opts);
      }
    }

    function upsertLayer(spec: maplibregl.LayerSpecification) {
      if (map.getLayer(spec.id)) {
        // Update paint if changed (simplest: remove + re-add)
        map.removeLayer(spec.id);
      }
      map.addLayer(spec);
    }

    // Segments (lines) — colored by plan lane, width by cable_kind.
    // promoteId='mb_uid' so feature.id is the segment's mb_uid for filters +
    // click lookups. Required for the red-glow filter and the popup to work.
    upsertSource('seg-src', data.segments, 'mb_uid');

    // Bucket segments by remaining-blocker count so the operator can see
    // PROGRESS on the map — not just a binary "blocked or not" flag. Three
    // tiers: heavily blocked (red), partially blocked (amber), permitted
    // (no glow). When all permits for a segment clear, the glow disappears
    // entirely and a green border tells the operator it's ready to build.
    // IDs in these arrays must match feature.id after promoteId. We promoted
    // mb_uid, so push the mb_uid (not the DB segment_id) into the filter lists.
    const heavilyBlocked: string[] = [];   // 4+ remaining blockers
    const partiallyBlocked: string[] = []; // 1-3 remaining blockers
    const cleared: string[] = [];          // 0 blockers, status='planned' or 'permitted'
    for (const sr of (readiness?.segments ?? [])) {
      const n = sr.blocking_permits?.length ?? 0;
      const key = sr.mb_uid ?? sr.segment_id;
      if (!key) continue;
      if (n >= 4) heavilyBlocked.push(key);
      else if (n >= 1) partiallyBlocked.push(key);
      else if (sr.status === 'planned' || sr.status === 'permitted') cleared.push(key);
    }
    const inIds = (ids: string[]) =>
      ids.length > 0 ? ['in', ['id'], ['literal', ids]] : ['==', ['get', '__never__'], true];

    // RED GLOW — 4+ remaining blockers. The "nothing's going to happen here
    // anytime soon" tier.
    upsertLayer({
      id: 'seg-line-blocked-heavy',
      type: 'line',
      source: 'seg-src',
      filter: ['all', laneFilterExpr(), inIds(heavilyBlocked)] as any,
      paint: {
        'line-color': '#ef4444',
        'line-width': [
          'match', ['get', 'cable_kind'],
          'backbone', 10, 'distribution', 8, 'pigtail', 5, 5,
        ],
        'line-opacity': 0.4,
        'line-blur': 2,
      },
      layout: { visibility: visibleLayers.segments ? 'visible' : 'none' },
    });

    // AMBER GLOW — 1-3 remaining blockers. "Almost there, push through these."
    upsertLayer({
      id: 'seg-line-blocked-partial',
      type: 'line',
      source: 'seg-src',
      filter: ['all', laneFilterExpr(), inIds(partiallyBlocked)] as any,
      paint: {
        'line-color': '#f59e0b',
        'line-width': [
          'match', ['get', 'cable_kind'],
          'backbone', 10, 'distribution', 8, 'pigtail', 5, 5,
        ],
        'line-opacity': 0.4,
        'line-blur': 2,
      },
      layout: { visibility: visibleLayers.segments ? 'visible' : 'none' },
    });

    // GREEN HALO — 0 blockers. The "this can be bored today" tier.
    upsertLayer({
      id: 'seg-line-cleared',
      type: 'line',
      source: 'seg-src',
      filter: ['all', laneFilterExpr(), inIds(cleared)] as any,
      paint: {
        'line-color': '#10b981',
        'line-width': [
          'match', ['get', 'cable_kind'],
          'backbone', 10, 'distribution', 8, 'pigtail', 5, 5,
        ],
        'line-opacity': 0.35,
        'line-blur': 1,
      },
      layout: { visibility: visibleLayers.segments ? 'visible' : 'none' },
    });

    upsertLayer({
      id: 'seg-line',
      type: 'line',
      source: 'seg-src',
      filter: laneFilterExpr(),
      paint: {
        'line-color': planColorExpr(),
        'line-width': [
          'match', ['get', 'cable_kind'],
          'backbone', 4, 'distribution', 3, 'pigtail', 1.5, 1.5,
        ],
        'line-opacity': 0.85,
      },
      layout: { visibility: visibleLayers.segments ? 'visible' : 'none' },
    });

    // Drops (lines, thinner) — same color logic
    upsertSource('drop-src', data.drops);
    upsertLayer({
      id: 'drop-line',
      type: 'line',
      source: 'drop-src',
      filter: laneFilterExpr(),
      paint: {
        'line-color': planColorExpr(),
        'line-width': 1.2,
        'line-opacity': 0.7,
        'line-dasharray': [2, 2],
      },
      layout: { visibility: visibleLayers.drops ? 'visible' : 'none' },
    });

    // Ducts (lines)
    upsertSource('duct-src', data.ducts);
    upsertLayer({
      id: 'duct-line',
      type: 'line',
      source: 'duct-src',
      filter: laneFilterExpr(),
      paint: {
        'line-color': '#a855f7',
        'line-width': 2,
        'line-opacity': 0.5,
        'line-dasharray': [4, 2],
      },
      layout: { visibility: visibleLayers.ducts ? 'visible' : 'none' },
    });

    // Poles (circles)
    upsertSource('pole-src', data.poles);
    upsertLayer({
      id: 'pole-pt',
      type: 'circle',
      source: 'pole-src',
      filter: laneFilterExpr(),
      paint: {
        'circle-radius': 4,
        'circle-color': '#92400e',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1,
      },
      layout: { visibility: visibleLayers.poles ? 'visible' : 'none' },
    });

    // Vaults (squares — use circle with smaller radius + different color)
    upsertSource('vault-src', data.vaults);
    upsertLayer({
      id: 'vault-pt',
      type: 'circle',
      source: 'vault-src',
      filter: laneFilterExpr(),
      paint: {
        'circle-radius': 5,
        'circle-color': '#0f766e',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1.5,
      },
      layout: { visibility: visibleLayers.vaults ? 'visible' : 'none' },
    });

    // Closures (larger, distinct)
    upsertSource('closure-src', data.closures);
    upsertLayer({
      id: 'closure-pt',
      type: 'circle',
      source: 'closure-src',
      filter: laneFilterExpr(),
      paint: {
        'circle-radius': 7,
        'circle-color': '#ef4444',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2,
      },
      layout: { visibility: visibleLayers.closures ? 'visible' : 'none' },
    });

    // Structures
    upsertSource('struct-src', data.structures);
    upsertLayer({
      id: 'struct-pt',
      type: 'circle',
      source: 'struct-src',
      filter: laneFilterExpr(),
      paint: {
        'circle-radius': 8,
        'circle-color': '#1e293b',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2,
      },
      layout: { visibility: visibleLayers.structures ? 'visible' : 'none' },
    });

    // Equipment
    upsertSource('equip-src', data.equipment);
    upsertLayer({
      id: 'equip-pt',
      type: 'circle',
      source: 'equip-src',
      filter: laneFilterExpr(),
      paint: {
        'circle-radius': 5,
        'circle-color': '#a855f7',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1.5,
      },
      layout: { visibility: visibleLayers.equipment ? 'visible' : 'none' },
    });

    // Fit to data bounds on first load
    try {
      const allFeatures: any[] = [];
      for (const k of ['segments','poles','vaults','closures','drops','structures','equipment','ducts'] as const) {
        if (data[k]?.features?.length) allFeatures.push(...data[k].features);
      }
      if (allFeatures.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        for (const f of allFeatures) {
          const g = f.geometry;
          if (!g) continue;
          if (g.type === 'Point') bounds.extend(g.coordinates);
          else if (g.type === 'LineString') for (const c of g.coordinates) bounds.extend(c);
        }
        if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 40, maxZoom: 17, duration: 0 });
      }
    } catch (e) { /* tolerate */ }

  }, [mapReady, data, visibleLayers, visibleLanes, planColorMap, readiness]);

  // Click handler — wired once
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const layerToKind: Record<string, string> = {
      'seg-line': 'segment', 'drop-line': 'drop', 'duct-line': 'duct',
      'pole-pt': 'pole', 'vault-pt': 'vault', 'closure-pt': 'closure',
      'struct-pt': 'structure', 'equip-pt': 'equipment',
    };
    // Click handler — opens the parent's FeatureDetailDrawer. We used to
    // ALSO show a MapLibre popup with permit detail next to the click, but
    // that popup duplicated the drawer and didn't close when the drawer did,
    // leaving a confusing orphan on the map. Drawer-only path is simpler.
    const handleClick = (e: maplibregl.MapMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: Object.keys(layerToKind),
      });
      if (features.length === 0) return;
      const f = features[0] as any;
      const kind = layerToKind[f.layer.id] ?? '?';
      const props = f.properties ?? {};
      onFeatureClick?.({
        kind,
        id: f.id,
        mb_uid: props.mb_uid,
        source_plan_id: props.source_plan_id != null ? Number(props.source_plan_id) : null,
        status: props.status,
        props,
      });
    };
    map.on('click', handleClick);
    // Cursor change on hover
    const handleEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const handleLeave = () => { map.getCanvas().style.cursor = ''; };
    for (const lid of Object.keys(layerToKind)) {
      map.on('mouseenter', lid, handleEnter);
      map.on('mouseleave', lid, handleLeave);
    }
    return () => {
      map.off('click', handleClick);
      for (const lid of Object.keys(layerToKind)) {
        map.off('mouseenter', lid, handleEnter);
        map.off('mouseleave', lid, handleLeave);
      }
    };
  }, [mapReady, onFeatureClick, segReadinessMap]);

  const lanes = data?.plan_lanes ?? [];

  return (
    <div style={{ position: 'relative', height, width: '100%', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />

      {/* Layer panel — top-left floating */}
      <div style={{
        position: 'absolute', top: 12, left: 12, zIndex: 10,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)', padding: '10px 14px',
        fontSize: 12, fontFamily: 'system-ui', minWidth: 200,
        maxHeight: 'calc(100% - 40px)', overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Layers</div>
        {Object.entries(visibleLayers).map(([k, v]) => {
          const counts: Record<string, number> = {
            segments: data?.segments?.features?.length ?? 0,
            drops: data?.drops?.features?.length ?? 0,
            poles: data?.poles?.features?.length ?? 0,
            vaults: data?.vaults?.features?.length ?? 0,
            closures: data?.closures?.features?.length ?? 0,
            structures: data?.structures?.features?.length ?? 0,
            equipment: data?.equipment?.features?.length ?? 0,
            ducts: data?.ducts?.features?.length ?? 0,
          };
          return (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer', fontSize: 12, opacity: counts[k] > 0 ? 1 : 0.5 }}>
              <input
                type="checkbox" checked={v}
                onChange={(e) => setVisibleLayers(s => ({ ...s, [k]: e.target.checked }))}
              />
              <span style={{ flex: 1, textTransform: 'capitalize' }}>{k}</span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>{counts[k]}</span>
            </label>
          );
        })}

        {readiness && (
          <>
            <div style={{ fontWeight: 700, marginTop: 10, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Permit readiness</div>
            <div style={{ fontSize: 11, lineHeight: 1.7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 18, height: 4, background: '#ef4444', opacity: 0.6, borderRadius: 2 }} />
                <span>Heavy (4+ permits needed)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 18, height: 4, background: '#f59e0b', opacity: 0.6, borderRadius: 2 }} />
                <span>Partial (1–3 permits needed)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 18, height: 4, background: '#10b981', opacity: 0.6, borderRadius: 2 }} />
                <span>Clear (ready to build)</span>
              </div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                Click any segment for blocking-permit details.
              </div>
            </div>
          </>
        )}

        {lanes.length > 0 && (
          <>
            <div style={{ fontWeight: 700, marginTop: 10, marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Plan lanes</div>
            {lanes.map((l, i) => {
              const color = planColorMap.get(l.source_plan_id) ?? '#94a3b8';
              const vis = visibleLanes[l.source_plan_id] ?? true;
              return (
                <label key={l.source_plan_id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer', fontSize: 12 }}>
                  <input
                    type="checkbox" checked={vis}
                    onChange={(e) => setVisibleLanes(s => ({ ...s, [l.source_plan_id]: e.target.checked }))}
                  />
                  <span style={{ width: 12, height: 12, background: color, borderRadius: 2, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.source_project_label ?? `Plan ${l.source_plan_id}`}>
                    {l.source_project_label ?? `Plan ${l.source_plan_id}`}
                  </span>
                  {l.is_canonical && <span style={{ fontSize: 10, color: '#065f46' }}>★</span>}
                </label>
              );
            })}
          </>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ position: 'absolute', top: 12, right: 12, padding: '4px 10px', background: '#fff', borderRadius: 4, fontSize: 11, color: '#64748b', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
          Loading map data…
        </div>
      )}
    </div>
  );
}
